import { ContactSource } from "client/Api/Email/contacts";
import {
	Highlight,
	HighlightedText,
	ListItem,
	PersonAvatar,
	PipeSeparator,
	Space,
	Typography
} from "client/Components";
import { normalizeForSearch } from "client/Data/searching";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { contactSourceToSourceOptionMap } from "client/Features/AddressBook/SourceSelector";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useMemo } from "react";
import { useHighlights } from "../ResultView/CategoryView/ItemView/useHighlights";
import { HighlightItem } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { useGlobalSearchItemActions } from "../GlobalSearchItem/useGlobalSearchItemActions";


export type ContactListItemHighlightConfiguration = {
	hitHighlights: readonly HighlightItem[];
	searchTerm: string;
};

export type ContactListItemProps = {
	contactSource: ContactSource;
	jobTitle: string | null;
	department: string | null;
	company: string | null;
	id: string;
	photoUrl: string | null;
	firstName: string | null;
	middleName: string | null;
	lastName: string | null;
	displayName: string | null;
	phoneNumber: string | null;
	emailAddress: string | null;
	highlightConfiguration?: ContactListItemHighlightConfiguration;
	onRemove?: () => void;
	itemIndex: number;
	tabIndex: number;
	onExecute: () => void;
};

export const ContactListItem = (props: ContactListItemProps): ReactElement => {
	const {
		id,
		contactSource,
		highlightConfiguration,
		jobTitle,
		department,
		company,
		photoUrl,
		firstName,
		middleName,
		lastName,
		displayName,
		emailAddress,
		phoneNumber,
		onRemove,
		itemIndex,
		tabIndex,
		onExecute,
	} = props;

	let hitHighlights: readonly HighlightItem[];
	let searchTerm: string | undefined;

	if (highlightConfiguration) {
		hitHighlights = highlightConfiguration.hitHighlights;
		searchTerm = highlightConfiguration.searchTerm;
	} else {
		hitHighlights = EMPTY_ARRAY;
		searchTerm = undefined;
	}

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const contactHighlights = useMemo(
		(): Map<string, Highlight> => {
			return fromArray(
				[...highlights.entries()]
					.filter(([key]) => key.startsWith("contact.")),
				([key]) => key.slice("contact.".length),
				([_, value]) => value
			);
		},
		[highlights]
	);

	const {
		computedDisplayName,
		initials,
		highlight
	} = useMemo(
		() => {
			return getComputedDisplayName(
				{
					firstName,
					middleName,
					lastName,
					displayName,
					company,
					emailAddress,
					phoneNumber
				},
				contactHighlights
			);
		},
		[
			company,
			contactHighlights,
			displayName,
			emailAddress,
			firstName,
			lastName,
			middleName,
			phoneNumber
		]
	);

	const computedDisplayNameHighlight = useMemo(
		(): Highlight | undefined => {
			if (highlight) {
				return highlight;
			}

			if (!searchTerm) {
				return undefined;
			}

			const termIndex: number = normalizeForSearch(computedDisplayName)
				.indexOf(
					normalizeForSearch(searchTerm)
				);

			return termIndex !== -1
				? [termIndex, searchTerm.length]
				: undefined;
		},
		[
			computedDisplayName,
			highlight,
			searchTerm
		]
	);

	const {
		onKeyDown,
		ref
	} = useGlobalSearchItemActions({
		itemIndex,
		onExecute: onExecute
	});

	return (
		<ListItem
			trackingId={generateTrackingId("contact-result-list-item", `|${id}`)}
			noMobileBorderBottom
			tabIndex={tabIndex}
			ref={ref}
			onKeyDown={onKeyDown}
		>
			<ListItem.Content
				thumbnail={
					<PersonAvatar
						trackingId="icon"
						initials={initials}
						imageSrc={photoUrl}
						size="small"
					/>
				}
			>
				<ListItem.Title>
					<Typography
						weight="bold"
						trackingId="title"
					>
						<HighlightedText
							text={computedDisplayName}
							highlight={computedDisplayNameHighlight}
						/>
					</Typography>
				</ListItem.Title>
				<ListItem.SubTitle>
					{jobTitle && (
						<>
							<HighlightedText
								text={jobTitle}
								highlight={contactHighlights.get("jobTitle")}
							/>
							<PipeSeparator />
						</>
					)}

					{department && (
						<>
							<HighlightedText
								text={department}
								highlight={contactHighlights.get("department")}
							/>
							<PipeSeparator />
						</>
					)}

					<SourceLabel source={contactSource} />
				</ListItem.SubTitle>
			</ListItem.Content>
			{isNotNil(onRemove) && (
				<ListItem.Action
					icon="xmark"
					onClick={() => onRemove()}
					bubbleClick={false}
					trackingId="remove"
				/>
			)}
		</ListItem>
	);
};
ContactListItem.displayName = "ContactListItem";

type SourceLabelProps = {
	source: ContactSource;
};

const SourceLabel = (props: SourceLabelProps): ReactElement | null => {
	const {
		source
	} = props;

	if (source === ContactSource.Suggested) {
		return null;
	}

	const { icon, textKey } = contactSourceToSourceOptionMap[source];

	return (
		<>
			<ListItem.SubTitleIcon kind={icon} />
			<Space />
			{localize(textKey)}
		</>
	);
};
SourceLabel.displayName = "SourceLabel";
